/**
 * Motorul de oferte Zafra — partea de client (portat din artevita-offers.js).
 *
 * Alegerea ofertei se face in Liquid (snippets/zafra-offer.liquid), deci aici
 * nu se decide NIMIC despre ce se afiseaza. Scriptul face doar trei lucruri:
 *   1. numaratoarea inversa pentru ofertele cu timer (si ascunde oferta la 0);
 *   2. progresul live „mai adauga N produse" pentru ofertele de tip pachet,
 *      citind cosul real;
 *   3. butonul „adauga 2 flacoane", care refoloseste formularul temei.
 */

const SEL_TIMER = '[data-zf-timer]';
const SEL_BUNDLE = '[data-zf-bundle]';
const SEL_ADD = '[data-zf-add-qty]';

const pad = (n) => String(n).padStart(2, '0');

/* ------------------------------- timer ---------------------------------- */

/*
  Doua moduri, ca la GOF (Grandia / Lab Noir):
   · `data`   — numaratoare globala pana la un moment fix. Aceeasi pentru toti.
   · `durata` — fiecare vizitator primeste fereastra lui (ex. 7h), tinuta intr-un
                cookie, ca urgenta sa ramana stransa in loc sa arate „174 ore".
                Cookie-ul traieste 24h; daca a expirat, fereastra reporneste.
*/
function readCookie(name) {
  const row = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
  return row ? Number(row.split('=')[1]) : NaN;
}

function resolveEnd(el) {
  const mode = el.dataset.zfMode === 'durata' ? 'durata' : 'data';

  if (mode === 'data') {
    const ends = Number(el.dataset.zfEnds) * 1000;
    return Number.isFinite(ends) && ends > 0 ? ends : NaN;
  }

  const duration = Number(el.dataset.zfDuration) * 1000;
  if (!Number.isFinite(duration) || duration <= 0) return NaN;

  const cookie = 'zfOfferTimer_' + (el.dataset.zfKey || 'default');
  const saved = readCookie(cookie);
  const now = Date.now();

  if (Number.isFinite(saved) && saved > now) return saved;

  const ends = now + duration;
  document.cookie = `${cookie}=${ends}; path=/; max-age=86400; SameSite=Lax`;
  return ends;
}

function startTimer(el) {
  if (el.dataset.zfTimerOn === '1') return;
  el.dataset.zfTimerOn = '1';

  const endsAt = resolveEnd(el);
  if (!Number.isFinite(endsAt)) return;

  const offer = el.closest('.zf-offer');
  const dTile = el.querySelector('[data-zf-d-tile]');
  const d = el.querySelector('[data-zf-d]');
  const h = el.querySelector('[data-zf-h]');
  const m = el.querySelector('[data-zf-m]');
  const s = el.querySelector('[data-zf-s]');

  /*
    Unitatile pluralizate („1 hour" / „2 hours", cu few/many unde limba cere)
    vin pre-randate din Liquid pe fiecare valoare 0–99; fara ele, eticheta
    statica din markup ramane cum e.
  */
  const units = readJson(el.dataset.zfUnits);
  const unitEls = {
    d: el.querySelector('[data-zf-unit="d"]'),
    h: el.querySelector('[data-zf-unit="h"]'),
    m: el.querySelector('[data-zf-unit="m"]'),
    s: el.querySelector('[data-zf-unit="s"]'),
  };
  const setUnit = (key, n) => {
    const list = units?.[key];
    const target = unitEls[key];
    if (!Array.isArray(list) || !target || list.length === 0) return;
    const label = list[n] ?? list[n % 100] ?? list[list.length - 1];
    if (typeof label === 'string' && target.textContent !== label) target.textContent = label;
  };

  const tick = () => {
    const left = endsAt - Date.now();

    if (left <= 0) {
      // `durata`: fereastra per vizitator reporneste (ca la GOF) in loc sa ascunda oferta.
      if (el.dataset.zfMode === 'durata') {
        document.cookie = 'zfOfferTimer_' + (el.dataset.zfKey || 'default') + '=; path=/; max-age=0; SameSite=Lax';
        clearInterval(id);
        el.dataset.zfTimerOn = '';
        startTimer(el);
        return;
      }
      clearInterval(id);
      if (offer) offer.hidden = true;
      return;
    }

    const total = Math.floor(left / 1000);
    const days = Math.floor(total / 86400);

    if (dTile) dTile.hidden = days === 0;
    if (d) d.textContent = String(days);

    const hours = days > 0 ? Math.floor((total % 86400) / 3600) : Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (h) h.textContent = pad(hours);
    if (m) m.textContent = pad(minutes);
    if (s) s.textContent = pad(seconds);

    if (units) {
      setUnit('d', days);
      setUnit('h', hours);
      setUnit('m', minutes);
      setUnit('s', seconds);
    }
  };

  tick();
  const id = setInterval(tick, 1000);
}

/* ------------------------------- pachet --------------------------------- */

let cartPromise = null;
let cartSignature = null;
let refreshToken = 0;

function getCart() {
  if (!cartPromise) {
    cartPromise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return cartPromise;
}

/*
  Textele vin din Liquid (cheile `duppo.offer.*`), deja traduse si pluralizate pe
  server pentru fiecare numar posibil — JS-ul doar alege sirul potrivit. Asa
  merg si limbile cu mai multe forme de plural (pl, cs, sk, hr, sl).
*/
function readJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

async function refreshBundle(el) {
  const need = Number(el.dataset.zfNeed) || 3;
  const ids = new Set(
    (el.dataset.zfIds || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  );

  const cart = await getCart();
  if (cartSignature === null) cartSignature = signature(cart);
  let have = 0;

  if (cart && Array.isArray(cart.items)) {
    for (const item of cart.items) {
      if (ids.has(String(item.product_id))) have += item.quantity;
    }
  }

  const steps = el.querySelectorAll('[data-zf-step]');
  steps.forEach((step, i) => {
    if (i < have) step.setAttribute('data-zf-on', '');
    else step.removeAttribute('data-zf-on');
  });

  const text = el.querySelector('[data-zf-bundle-text]');
  if (!text) return;

  /*
    Titlul cardului spune deja oferta intreaga, deci aici ramane doar starea —
    altfel linia de progres o repeta cuvant cu cuvant.
  */
  const missing = need - have;
  const msgs = readJson(el.dataset.zfMsgs);
  if (!msgs) return;

  /* Tipul `bxgy` (2+1) si `bundle_qty` primesc fiecare setul lor de mesaje din Liquid. */
  const next = missing <= 0 ? msgs.done : msgs.missing?.[String(missing)];
  if (typeof next === 'string' && next) text.textContent = next;
}

/* ------------------------- adauga N in cos ------------------------------ */

function wireAddButton(button) {
  if (button.dataset.zfWired === '1') return;
  button.dataset.zfWired = '1';

  button.addEventListener('click', () => {
    const qty = Number(button.dataset.zfAddQty) || 2;
    const form = document.querySelector('product-form-component');
    const cta = form?.querySelector('add-to-cart-component button, button[type="submit"]');
    if (!form || !cta) return;

    /*
      Tema citeste cantitatea din `quantity-selector-component` (public:
      getValue/setValue) si abia apoi cade pe `data-quantity-default`. Daca
      selectorul exista pe pagina, el are ultimul cuvant — deci pe el il setam.
    */
    const selector = form.querySelector('quantity-selector-component');
    const previousQty = typeof selector?.getValue === 'function' ? selector.getValue() : null;
    const previousDefault = form.dataset.quantityDefault;

    if (typeof selector?.setValue === 'function') selector.setValue(qty);
    else form.dataset.quantityDefault = String(qty);

    button.setAttribute('aria-busy', 'true');
    cta.click();

    setTimeout(() => {
      if (previousQty !== null && typeof selector?.setValue === 'function') {
        selector.setValue(previousQty);
      } else if (previousDefault === undefined) {
        delete form.dataset.quantityDefault;
      } else {
        form.dataset.quantityDefault = previousDefault;
      }
      button.removeAttribute('aria-busy');
    }, 1500);
  });
}

/* --------------------------------- init --------------------------------- */

function scan(root = document) {
  root.querySelectorAll(SEL_TIMER).forEach(startTimer);
  root.querySelectorAll(SEL_BUNDLE).forEach(refreshBundle);
  root.querySelectorAll(SEL_ADD).forEach(wireAddButton);
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function signature(cart) {
  if (!cart || !Array.isArray(cart.items)) return '';
  return `${cart.item_count}|${cart.items.map((i) => `${i.product_id}x${i.quantity}`).join(',')}`;
}

/*
  Tema emite evenimentul FARA payload si inainte ca mutatia de cos sa fie
  comisa, deci o singura re-citire intoarce cosul vechi (progresul ramanea cu un
  pas in urma). Re-citim pana se schimba efectiv continutul, cu un plafon.
*/
async function onCartChanged() {
  const token = ++refreshToken;

  for (let attempt = 0; attempt < 4; attempt++) {
    await sleep(attempt === 0 ? 250 : 450);
    if (token !== refreshToken) return;

    cartPromise = null;
    const cart = await getCart();
    const next = signature(cart);

    if (next !== cartSignature) {
      cartSignature = next;
      break;
    }
  }

  if (token !== refreshToken) return;
  document.querySelectorAll(SEL_BUNDLE).forEach(refreshBundle);
}

scan();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  scan(target instanceof Element ? target : document);
});

/*
  Tema emite `shopify:cart:lines-update` (verificat pe live). Il ascultam direct,
  ca sa nu depindem de CDN-ul Shopify; importul de mai jos ramane doar ca plasa
  de siguranta daca numele evenimentului se schimba intr-o versiune viitoare.
*/
for (const name of ['shopify:cart:lines-update', 'shopify:cart:update', 'cart:update']) {
  document.addEventListener(name, onCartChanged);
}

import('@shopify/events')
  .then((mod) => {
    const name = mod?.StandardEvents?.cartLinesUpdate;
    if (name) document.addEventListener(name, onCartChanged);
  })
  .catch(() => {});
